import { FieldTypeId } from "@models/configuration";
import {
  MAAnalysisBreakdownDTO,
  MAAnalysisSectionDTO,
  SelectedSubProductDTO,
} from "@models/managed-accounts/entities/step/fee";
import { AnalysisDTO } from "@models/platform-analysis/entities/analysis";
import { BusinessMetricCategories } from "@models/platform-analysis/entities/steps/businessMetric";
import {
  AnalysisFeatureAllDataDTO,
  AnalysisFeatureDTO,
  AnalysisSectionDTO,
  FeatureAnalysisRowDTO,
  OverallScoreDTO,
} from "@models/platform-analysis/entities/steps/feature";
import { FeeSubProductDTO } from "@models/platform-analysis/entities/steps/fee";
import { AnalysisDataValue } from "@models/platform-analysis/enums/dataValue";
import { FeatureAnalysisEssentialFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisEssentialFilter";
import { FeatureAnalysisAvailabilityFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisFilter";
import { FeatureAnalysisRowLevel } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { FeesDisplayMode } from "@models/platform-analysis/enums/fee/displayMode";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "@models/platform-analysis/enums/fee/missingInvestments";
import { AnalysisStatus } from "@models/platform-analysis/enums/status";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { FeeAnalysisPortfolioFeesConstants } from "@pages/platform-analysis-v2/_id/steps/fee/analysis/constants";
import { PlatformAnalysisStore } from "@redux/slices/platform-analysis";
import { chunk, defaultTo, isEmpty, isNil } from "lodash";

export const repairUpdateAnalysis = (analysis: AnalysisDTO): AnalysisDTO => {
  return {
    ...analysis,
    setup: null,
    feature: null,
    businessMetric: null,
    fee: null,
    summary: null,
  };
};

export const updateReadOnlyAndComplete = (
  state: PlatformAnalysisStore,
  data: {
    isCompleted?: boolean;
    status?: AnalysisStatus;
  },
) => {
  //Set isCompleted
  const newState = { ...state };
  newState.isCompleted = data.isCompleted;
  //Set isReadOnly
  newState.isReadOnly =
    data.status === AnalysisStatus.Archived || newState.isCompleted;
  //Set hidden Selection steps when isReadOnly
  if (
    newState.ui.steps.Feature.subSteps?.Selection.isHidden !== undefined &&
    newState.isReadOnly
  ) {
    newState.ui.steps.Feature.subSteps.Selection.isHidden = true;
  }
  if (
    newState.ui.steps.BusinessMetric.subSteps?.Selection.isHidden !==
      undefined &&
    newState.isReadOnly
  ) {
    newState.ui.steps.BusinessMetric.subSteps.Selection.isHidden = true;
  }
  if (
    newState.ui.steps.Fee.subSteps?.Selection.isHidden !== undefined &&
    newState.isReadOnly
  ) {
    newState.ui.steps.Fee.subSteps.Selection.isHidden = true;
  }
  if (newState.ui.selectedSubStepIndex === 1 && newState.isReadOnly) {
    newState.ui.selectedSubStepIndex = 0;
  }

  return newState;
};

export const compileFeatureAnalysisRows = ({
  sections,
  overallScore,
  insertOverallScoreRow,
}: {
  sections: AnalysisSectionDTO[];
  overallScore?: OverallScoreDTO;
  insertOverallScoreRow?: boolean;
}): FeatureAnalysisRowDTO[] => {
  const resultRows: FeatureAnalysisRowDTO[] = [];

  if (insertOverallScoreRow && overallScore) {
    const overallScoreRow: FeatureAnalysisRowDTO = {
      level: FeatureAnalysisRowLevel.Section,
      id: "overall-score",
      name: "Overall score",
      analysisData: overallScore.analysisData || [],
      totalFeatures: overallScore.totalFeature || 0,
      totalFeaturesSelected: overallScore.totalSelectedFeature || 0,
    };

    resultRows.push(overallScoreRow);
  }

  const rows = sections?.map((section) => createSectionRow(section)) || [];
  resultRows.push(...rows);

  return resultRows;
};

export const compileBusinessMetricAnalysisRows = compileFeatureAnalysisRows;

export const splitFeatureAnalysis = (
  separateNum: number,
  analysisData?: AnalysisFeatureAllDataDTO,
): AnalysisFeatureAllDataDTO[] => {
  if (isNil(analysisData)) return [];

  return Array.from(
    Array(Math.ceil(analysisData.selectedProducts.length / separateNum)).keys(),
  ).map((index) => {
    const selectedProductsChunk = chunk(
      analysisData.selectedProducts,
      separateNum,
    )[index];
    const overallScoreChunk = chunk(
      analysisData.overallScore.analysisData,
      separateNum,
    )[index];
    const sections = analysisData.sections.map((section) => ({
      ...section,
      analysisData: chunk(section.analysisData, separateNum)[index],
      features: section.features?.map((feature) => ({
        ...feature,
        analysisData: chunk(feature.analysisData, separateNum)[index],
      })),
      subSections: section?.subSections?.map((subSection) => ({
        ...subSection,
        analysisData: chunk(subSection.analysisData, separateNum)[index],
        features: subSection?.features?.map((feature) => ({
          ...feature,
          analysisData: chunk(feature.analysisData, separateNum)[index],
        })),
      })),
    }));

    return {
      ...analysisData,
      selectedProducts: selectedProductsChunk,
      overallScore: {
        ...analysisData.overallScore,
        analysisData: overallScoreChunk,
      },
      sections: sections,
    };
  }) as AnalysisFeatureAllDataDTO[];
};

export const compileFeeAnalysisSmaPortfolioRows = ({
  sections,
  subProducts,
}: {
  sections: MAAnalysisSectionDTO[];
  subProducts?: SelectedSubProductDTO[];
}): FeatureAnalysisRowDTO[] => {
  const productIdsMap: Record<string, string> = {};

  subProducts?.forEach((sp) => {
    const key = `${sp.id}-${sp.rateCardVersion ?? 0}`;
    productIdsMap[key] = sp.productId || "";
  });

  return (
    sections?.map((section) => createSmaSectionRow(section, productIdsMap)) ||
    []
  );
};

export const filterFeatureAnalysisRows = ({
  rows,
  essentialFilterLevel,
  availabilityFilterLevel,
}: {
  rows: FeatureAnalysisRowDTO[];
  essentialFilterLevel?: FeatureAnalysisEssentialFilterLevel;
  availabilityFilterLevel?: FeatureAnalysisAvailabilityFilterLevel;
}): FeatureAnalysisRowDTO[] => {
  const isFilterEssentialOnly =
    essentialFilterLevel === FeatureAnalysisEssentialFilterLevel.Essential;
  const isFilterNonEssentialOnly =
    essentialFilterLevel === FeatureAnalysisEssentialFilterLevel.NonEssential;
  const isFilterAvailableOnly =
    availabilityFilterLevel === FeatureAnalysisAvailabilityFilterLevel.Common;
  const isFilterUnavailableOnly =
    availabilityFilterLevel ===
    FeatureAnalysisAvailabilityFilterLevel.Differentiators;

  if (
    !isFilterEssentialOnly &&
    !isFilterNonEssentialOnly &&
    !isFilterAvailableOnly &&
    !isFilterUnavailableOnly
  ) {
    return rows;
  }

  const featurePassesFilter = (featureRow: FeatureAnalysisRowDTO): boolean => {
    if (isFilterEssentialOnly && !featureRow.isEssential) return false;
    if (isFilterNonEssentialOnly && featureRow.isEssential) return false;
    if (isFilterAvailableOnly && !featureRow.allFeatureAvailable) return false;
    if (isFilterUnavailableOnly && featureRow.allFeatureAvailable) return false;

    return true;
  };

  const result: FeatureAnalysisRowDTO[] = [];

  for (const sectionRow of rows) {
    const filteredSubSections: FeatureAnalysisRowDTO[] = [];

    if (sectionRow.subRows) {
      for (const subSectionRow of sectionRow.subRows) {
        if (!subSectionRow.subRows) continue;

        const filteredFeatures =
          subSectionRow.subRows.filter(featurePassesFilter);

        if (filteredFeatures.length > 0) {
          filteredSubSections.push({
            ...subSectionRow,
            subRows: filteredFeatures,
          });
        }
      }
    }

    if (filteredSubSections.length > 0) {
      result.push({
        ...sectionRow,
        subRows: filteredSubSections,
      });
    }
  }

  return result;
};

export const compileBusinessMetricItemSectionState = (
  data?: BusinessMetricCategories,
) => {
  const state: Record<string, boolean> = {};
  if (!data) return state;

  const { esg, financialStrength, operational } = data;

  esg?.forEach((item) => {
    state[item.id] = item.isSelected;
  });
  financialStrength?.forEach((item) => {
    state[item.id] = item.isSelected;
  });
  operational?.forEach((item) => {
    state[item.id] = item.isSelected;
  });

  return state;
};

export const filterFeeAnalysisTableRows = ({
  rows,
  performanceScope,
  displayMode,
}: {
  rows: FeatureAnalysisRowDTO[];
  performanceScope: FeesPerformanceScope;
  displayMode: FeesDisplayMode;
}): FeatureAnalysisRowDTO[] =>
  rows
    ?.filter(
      (row) =>
        !shouldHideFeeSmaRow({
          variable: row.variableName,
          performanceScope,
          displayMode,
        }),
    )
    ?.map((row) => ({
      ...row,
      subRows: filterFeeAnalysisTableRows({
        rows: row.subRows || [],
        performanceScope,
        displayMode,
      }),
    }));

export const filterFeeAnalysisSelectedSubProducts = (
  subProducts: FeeSubProductDTO[],
  investmentMenuFilter: string,
  missingInvestmentFilter: string,
): FeeSubProductDTO[] =>
  subProducts.filter((sp) => {
    const passesInvestmentMenuFilter =
      investmentMenuFilter === FeeInvestmentMenuOption.AllPlatform ||
      sp.investmentMenu === investmentMenuFilter ||
      sp.multiMenuAccounts;

    const passesMissingInvestmentFilter =
      missingInvestmentFilter === FeeMissingInvestmentsOption.AllPlatform ||
      !sp.warning;

    return passesInvestmentMenuFilter && passesMissingInvestmentFilter;
  });

export const buildHiddenIndexesMap = (
  allSubProducts: FeeSubProductDTO[],
  filteredSubProducts: FeeSubProductDTO[],
): boolean[] => {
  const visibleIds = new Set(filteredSubProducts.map((x) => x.id));

  return allSubProducts.map((subProduct) => !visibleIds.has(subProduct.id));
};

// #region Private Methods
const createFeatureRow = ({
  id = "",
  name = "",
  description = "",
  analysisData = [],
  isEssential = false,
}: AnalysisFeatureDTO): FeatureAnalysisRowDTO => {
  const allFeatureAvailable = !analysisData.some((data) =>
    [AnalysisDataValue.False, AnalysisDataValue.MissingData].includes(
      data.value as AnalysisDataValue,
    ),
  );

  return {
    level: FeatureAnalysisRowLevel.Feature,
    id: isEmpty(id) ? name : id,
    name,
    description: normalizeDescription(description),
    analysisData,
    isEssential,
    allFeatureAvailable,
    longestDataName: getLongestShortTextAnalysisDataValue(analysisData, name),
  };
};

const createSmaBreakdownRow = (
  {
    variableName = AnalysisVariable.None,
    name = "",
    description = "",
    analysisData = [],
  }: MAAnalysisBreakdownDTO,
  productIdsMap: Record<string, string>,
): FeatureAnalysisRowDTO => ({
  id: variableName,
  variableName: variableName as AnalysisVariable,
  name,
  longestDataName: getLongestShortTextAnalysisDataValue(analysisData, name),
  level: FeatureAnalysisRowLevel.Feature,
  description: normalizeDescription(description),
  analysisData: [],
  MAAnalysisData: analysisData?.map((data) => ({
    ...data,
    productId: productIdsMap[`${data.subProductId}-${data.rateCardVersion}`],
  })),
});

const createSubSectionRow = (
  {
    id = "",
    name = "",
    description = "",
    analysisData = [],
    features = [],
    totalFeature,
    totalSelectedFeature,
  }: AnalysisSectionDTO,
  shouldRemoveFeatureLevelRows = false,
): FeatureAnalysisRowDTO => ({
  level: FeatureAnalysisRowLevel.SubSection,
  id: isEmpty(id) ? name : id,
  name,
  longestDataName: getLongestShortTextAnalysisDataValue(analysisData, name),
  description: normalizeDescription(description),
  analysisData,
  subRows: shouldRemoveFeatureLevelRows ? [] : features.map(createFeatureRow),
  totalFeatures: totalFeature || 0,
  totalFeaturesSelected: totalSelectedFeature || 0,
});

const createSmaSubSectionRow = (
  {
    variableName = AnalysisVariable.None,
    name = "",
    description = "",
    analysisData = [],
    breakdowns = [],
  }: MAAnalysisBreakdownDTO,
  productIdsMap: Record<string, string>,
): FeatureAnalysisRowDTO => ({
  id: variableName,
  variableName: variableName as AnalysisVariable,
  level: FeatureAnalysisRowLevel.SubSection,
  name,
  longestDataName: getLongestShortTextAnalysisDataValue(analysisData, name),
  description: normalizeDescription(description),
  analysisData: [],
  MAAnalysisData: analysisData?.map((data) => ({
    ...data,
    productId: productIdsMap[`${data.subProductId}-${data.rateCardVersion}`],
  })),
  subRows: breakdowns.map((breakdown) =>
    createSmaBreakdownRow(breakdown, productIdsMap),
  ),
});

const createSectionRow = (
  {
    id = "",
    name = "",
    businessMetricTypeId,
    businessMetricTypeName,
    description = "",
    analysisData = [],
    subSections = [],
    features = [],
    iconUrl = "",
    totalFeature,
    totalSelectedFeature,
  }: AnalysisSectionDTO,
  shouldRemoveFeatureLevelRows = false,
): FeatureAnalysisRowDTO => ({
  level: FeatureAnalysisRowLevel.Section,
  id,
  businessMetricTypeId,
  name: businessMetricTypeName || name,
  longestDataName: getLongestShortTextAnalysisDataValue(analysisData, name),
  description: normalizeDescription(description),
  analysisData,
  iconUrl,
  totalFeatures: totalFeature || 0,
  totalFeaturesSelected: totalSelectedFeature || 0,
  subRows: [
    ...(shouldRemoveFeatureLevelRows ? [] : features.map(createFeatureRow)),
    ...subSections.map((subSection) =>
      createSubSectionRow(subSection, shouldRemoveFeatureLevelRows),
    ),
  ],
});

const createSmaSectionRow = (
  {
    variableName = AnalysisVariable.None,
    name = "",
    description = "",
    iconUrl = "",
    analysisData = [],
    subSections = [],
    breakdowns = [],
  }: MAAnalysisSectionDTO,
  productIdsMap: Record<string, string>,
): FeatureAnalysisRowDTO => ({
  id: variableName,
  variableName: variableName as AnalysisVariable,
  level: FeatureAnalysisRowLevel.Section,
  name,
  longestDataName: getLongestShortTextAnalysisDataValue(analysisData, name),
  description: normalizeDescription(description),
  iconUrl,
  analysisData: [],
  MAAnalysisData: analysisData?.map((data) => ({
    ...data,
    productId: productIdsMap[`${data.subProductId}-${data.rateCardVersion}`],
  })),
  subRows: [
    ...breakdowns.map((breakdown) =>
      createSmaBreakdownRow(breakdown, productIdsMap),
    ),
    ...subSections.map((subSection) =>
      createSmaSubSectionRow(subSection, productIdsMap),
    ),
  ],
});

const shouldHideFeeSmaRow = ({
  variable,
  performanceScope,
  displayMode,
}: {
  variable?: string;
  performanceScope: FeesPerformanceScope;
  displayMode: FeesDisplayMode;
}) => {
  if (!variable) return false;

  const shouldHideByScope =
    performanceScope === FeesPerformanceScope.Excluding &&
    FeeAnalysisPortfolioFeesConstants.ExcludingPerformanceFeeVariableNames.includes(
      variable,
    );

  const shouldHideByDisplayMode =
    displayMode === FeesDisplayMode.Summary &&
    !FeeAnalysisPortfolioFeesConstants.SummaryTableRowVariableNames.includes(
      variable,
    );

  return shouldHideByScope || shouldHideByDisplayMode;
};

const normalizeDescription = (description?: string | null) => {
  return description ? description.replace(/\u00a0/g, " ") : "";
};

const getLongestShortTextAnalysisDataValue = (
  analysisData: { fieldTypeId?: string; value?: string }[],
  rowName: string,
) => {
  if (
    !Array.isArray(analysisData) ||
    analysisData.length === 0 ||
    !("fieldTypeId" in analysisData[0])
  ) {
    return rowName;
  }

  let longest = "";
  let maxLength = rowName.length;

  for (const item of analysisData) {
    const isTextShort = item?.fieldTypeId === FieldTypeId.TextShort;
    const isMissingData = item?.value === AnalysisDataValue.MissingData;

    if (!isTextShort || isMissingData) continue;

    const length = defaultTo<number>(item?.value?.length, 0);

    if (length <= maxLength) continue;

    longest = item.value || "";
    maxLength = length;
  }

  return longest || rowName;
};
// #end region Private Methods
